import { useMemo } from 'react';
import {
  ComponentParamsOfType,
  RenderViewComponentParams,
  RenderViewComponentType,
} from '../../../../common/renderer/types';
import { useRenderViewContext } from './index';

type ComponentParamSetter<T extends RenderViewComponentType> = (
  updater: (prevParams: ComponentParamsOfType<T>) => ComponentParamsOfType<T>,
) => void;

/**
 * A hook that enables editing params of a single component. Useful especially for the WYSIWYG controls
 * that want to change how the component looks. To make this function usable within hooks without having to
 * worry about potential infinite dependency loops, it returns a function that can be used to provide updated
 * params based on current params, similarly to how React's setState(currState => {...}) logic works.
 *
 */
export function useGetRenderViewComponentParamSetter<T extends RenderViewComponentType>(
  type: T,
): ComponentParamSetter<T> {
  const { componentParams: paramsOfAllComponents, options, setOptions } = useRenderViewContext();
  const setComponentParamsOfType: ComponentParamSetter<T> = useMemo(() => {
    return function (updater) {
      const currParamsOfThisComponet = paramsOfAllComponents.find(d => d.componentType === type) as
        | ComponentParamsOfType<typeof type>
        | undefined;
      if (!currParamsOfThisComponet) throw Error(`Params for ${type} missing`);

      const newParamsOfThisComponent = updater(currParamsOfThisComponet);

      const updatedParamsOfAllComponents = mergeNewComponentParamsWithExisting(
        paramsOfAllComponents,
        newParamsOfThisComponent,
      );
      setOptions({ ...options, componentParams: updatedParamsOfAllComponents });
    };
  }, [paramsOfAllComponents, options, setOptions, type]);
  return setComponentParamsOfType;
}

function mergeNewComponentParamsWithExisting(
  allParams: RenderViewComponentParams[],
  newParams: RenderViewComponentParams,
) {
  if (!allParams.find(d => d.componentType === newParams.componentType)) {
    return [...allParams, newParams];
  }
  return allParams.map(d => {
    if (d.componentType === newParams.componentType) return newParams;
    return d;
  });
}
